import React, { memo } from 'react';
import Icon from '../components/Icon';

const PlayersSection = memo(({
  playerNames, updatePlayerName,
  playerHandicaps, updatePlayerHandicap,
  jumboMode, toggleJumboMode,
  gameMode, setGameMode,
  showModeDesc, setShowModeDesc,
  stake, setStake, stakeInputRef,
  activePlayers,
  showHcpTooltip, setShowHcpTooltip,
  startGame,
  setCurrentSection,
  t
}) => {
  return (
    <div className="space-y-4 py-3">
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-900">
                  {t('playerTitle')}
                </h2>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Icon name="users" size={16} />
                    {t('players')}
                  </h3>
                  <button
                    onClick={toggleJumboMode}
                    className={`px-3 py-1.5 rounded-md font-medium text-xs transition ${
                      jumboMode
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {t('mpJumbo')}
                  </button>
                </div>
                
                <div className="space-y-3">
                  {Array.from({ length: jumboMode ? 8 : 4 }, (_, i) => i).map(i => (
                    <div key={i}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t(`player${i + 1}`)}:
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder={t('enterName')}
                          value={playerNames[i]}
                          onChange={(e) => updatePlayerName(i, e.target.value)}
                          className="flex-1 px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        />
                        <div className="flex items-center gap-1 relative">
                          {i === 0 ? (
                            <span className="text-xs text-gray-500 border-b border-dashed border-gray-400 cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); setShowHcpTooltip(!showHcpTooltip); }}
                            >HCP</span>
                          ) : (
                            <span className="text-xs text-gray-500">HCP</span>
                          )}
                          {i === 0 && showHcpTooltip && (
                            <div className="absolute right-0 top-0 mt-[-8px] translate-y-[-100%] z-50 w-56 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg">
                              <div>{t('hcpBubble')}</div>
                              <div className="absolute bottom-[-4px] right-4 w-2 h-2 bg-gray-800 rotate-45"></div>
                            </div>
                          )}
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={2}
                            placeholder=""
                            value={(playerHandicaps[playerNames[i]] || 0) > 0 ? playerHandicaps[playerNames[i]] : ''}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              const num = val === '' ? 0 : Math.min(36, parseInt(val, 10));
                              if (playerNames[i]) {
                                updatePlayerHandicap(playerNames[i], num);
                              }
                            }}
                            className="w-12 px-2 py-2 rounded-md border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-green-500 text-sm text-center"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      {t('gameMode')}:
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => { setGameMode('matchPlay'); setShowModeDesc(showModeDesc && gameMode === 'matchPlay' ? false : true); }}
                        className={`px-3 rounded-lg font-medium text-sm transition flex flex-col items-center text-center gap-1 ${
                          gameMode === 'matchPlay'
                            ? `bg-blue-600 text-white ${showModeDesc ? 'py-3' : 'py-2'}`
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 py-2'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          <Icon name="trophy" size={16} />
                          <span style={{ fontSize: '12px' }}>{t('matchPlay')}</span>
                        </div>
                        {gameMode === 'matchPlay' && showModeDesc && (
                          <div className="text-xs font-normal opacity-90 mt-1 leading-snug" style={{ fontSize: '10px' }}>
                            {t('matchPlayBubble')}
                          </div>
                        )}
                      </button>
                      <button
                        onClick={() => { setGameMode('win123'); setShowModeDesc(showModeDesc && gameMode === 'win123' ? false : true); }}
                        className={`px-3 rounded-lg font-medium text-sm transition flex flex-col items-center text-center gap-1 ${
                          gameMode === 'win123'
                            ? `bg-green-600 text-white ${showModeDesc ? 'py-3' : 'py-2'}`
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 py-2'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          <Icon name="coin" size={16} />
                          <span style={{ fontSize: '12px' }}>{t('win123')}</span>
                        </div>
                        {gameMode === 'win123' && showModeDesc && (
                          <div className="text-xs font-normal opacity-90 mt-1 leading-snug" style={{ fontSize: '10px' }}>
                            {t('win123Bubble')}
                          </div>
                        )}
                      </button>
                      <button
                        onClick={() => { setGameMode('skins'); setShowModeDesc(showModeDesc && gameMode === 'skins' ? false : true); }}
                        className={`px-3 rounded-lg font-medium text-sm transition flex flex-col items-center text-center gap-1 ${
                          gameMode === 'skins'
                            ? `bg-purple-600 text-white ${showModeDesc ? 'py-3' : 'py-2'}`
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 py-2'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          <Icon name="coin" size={16} />
                          <span style={{ fontSize: '12px' }}>{t('skins')}</span>
                        </div>
                        {gameMode === 'skins' && showModeDesc && (
                          <div className="text-xs font-normal opacity-90 mt-1 leading-snug" style={{ fontSize: '10px' }}>
                            {t('skinsBubble')}
                          </div>
                        )}
                      </button>
                      <button
                        onClick={() => { setGameMode('baccarat'); setShowModeDesc(showModeDesc && gameMode === 'baccarat' ? false : true); }}
                        className={`px-3 rounded-lg font-medium text-sm transition flex flex-col items-center text-center gap-1 ${
                          gameMode === 'baccarat'
                            ? `bg-amber-600 text-white ${showModeDesc ? 'py-3' : 'py-2'}`
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 py-2'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          <Icon name="suit-spade" size={16} />
                          <span style={{ fontSize: '12px' }}>{t('baccarat')}</span>
                        </div>
                        {gameMode === 'baccarat' && showModeDesc && (
                          <div className="text-xs font-normal opacity-90 mt-1 leading-snug" style={{ fontSize: '10px' }}>
                            {t('baccaratBubble')}
                          </div>
                        )}
                      </button>
                    </div>
					</div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700">
                      {t('stake')}:
                    </label>
                    <input
                      ref={stakeInputRef}
                      type="number"
                      value={stake}
                      onChange={(e) => setStake(e.target.value)}
                      placeholder={t('enterStake')}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    />
                  </div>

                </div>
              </div>


              <div className="flex gap-3">
                <button
                  onClick={() => setCurrentSection('course')}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg font-medium hover:bg-gray-300"
                >
                  {t('back')}
                </button>
                
                <button
                  onClick={startGame}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700"
                >
                  {t('start')}
                </button>
              </div>
              
              {/* 加入房间按钮已移至首页 */}
            </div>
  );
});

export default PlayersSection;